







































import { mapActions, mapGetters } from 'vuex';

import { DeviceEnum, DomainEnum, FeatureIdEnum } from '../../enums';

import { SetContextWithUrlGuard } from '../../guards';

import { SteppedAnimationMixin } from '../../mixins/stepped-animation.mixin';

import { FeatureNode } from '../../models';

import { ROUTER_CONSTS } from '../../router/router.consts';

import { APP_CONSTS } from '../../store/app/app.consts';
import { CONTEXT_CONSTS } from '../../store/context/context.consts';

import { UiIconButton } from '../keen-ui';
import NavigationMenuFooter from './navigation-menu-footer.component.vue';
import NavigationMenuHeader from './navigation-menu-header.component.vue';
import NavigationMenuItem from './navigation-menu-item.component.vue';

export enum MenuSectionEnum {
  HEADER,
  BODY,
  FOOTER
}

export default {
  name: 'navigation-menu',
  props: {
    collapsible: Boolean,
    opened: Boolean
  },
  components: {
    NavigationMenuHeader,
    NavigationMenuItem,
    NavigationMenuFooter,
    UiIconButton
  },
  mixins: [SteppedAnimationMixin],
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
      collapsedInState: APP_CONSTS.getters.menuCollapsed
    }),
    collapsed: function (): boolean {
      return (this.collapsible && this.collapsedInState && !this.hovered);
    },
    domains: function (): DomainEnum[] {
      return Object.keys(this.featuresTree).reduce((domains: DomainEnum[], domainStr: string) => {
        if ((Object.values(DomainEnum) as string[]).includes(domainStr) && !!this.featuresTree[domainStr].length) {
          domains.push(domainStr as DomainEnum);
        }
        return domains;
      }, []);
    },
    collapsibleHeader: function (): boolean {
      return (this.collapsible && this.hovered && this.animationFinished);
    },
    isMobileDevice: function (): boolean {
      return (this.$epb.device === DeviceEnum.MOBILE);
    }
  },
  data: function () {
    return {
      selectedDomain: undefined,
      hovered: false,
      animationFinished: false,
      MenuSectionEnum
    };
  },
  methods: {
    ...mapActions(APP_CONSTS.moduleName, {
      setMenuCollapsed: APP_CONSTS.actions.setMenuCollapsed
    }),
    onMouseOver: function (): void {
      if (this.animationFinished) {
        this.hovered = true;
      }
    },
    onMouseLeave: function (): void {
      this.hovered = false;
    },
    setSelectedDomain: function (selectedDomain: DomainEnum): void {
      this.selectedDomain = selectedDomain;
    },
    updateCollapsedInState: function (collapsed: boolean): void {
      if (collapsed) {
        this.hovered = false;
      }
      this.setMenuCollapsed(collapsed);
    },
    getMenuItems: function (section: MenuSectionEnum): any[] {
      const menuItems = [];
      const { tree: filteredFeaturesTree, totalItems } = this.getFilteredFeaturesTree(section);
      let indexOfMenuItem = 0;
      Object.keys(filteredFeaturesTree).forEach((domainStr: string) => {
        const featureNodes = filteredFeaturesTree[domainStr];
        const domain = this.getDomainFromString(domainStr);
        featureNodes.forEach((featureNode: FeatureNode) => {
          const { item: menuItem, index: newIndexOfMenuItem } =
            this.getMenuItem(featureNode, domain, section, indexOfMenuItem, totalItems);
          menuItems.push(menuItem);
          indexOfMenuItem = newIndexOfMenuItem;
        });
      });
      return menuItems;
    },
    getFilteredFeaturesTree: function (section: MenuSectionEnum) {
      const filteredFeaturesTree = {};
      let totalItems = 0;
      Object.keys(this.featuresTree).forEach((domainStr: string) => {
        const domain = this.getDomainFromString(domainStr);
        if (!domain || (domain === this.selectedDomain)) {
          const featureNodes = this.featuresTree[domainStr];
          const { nodes: domainNodes, totalItems: domainTotalItems } = featureNodes.reduce((accum, featureNode: FeatureNode) => {
            const displayFeatureOnFooter = (!!featureNode.menuConfig && !!featureNode.menuConfig.displayOnFooter);
            if ((section === MenuSectionEnum.FOOTER) ? displayFeatureOnFooter : !displayFeatureOnFooter) {
              accum.nodes.push(featureNode);
              accum.totalItems += this.getFeatureNodeTotalItems(featureNode);
            }
            return accum;
          }, { nodes: [], totalItems: 0 });
          filteredFeaturesTree[domainStr] = domainNodes;
          totalItems += domainTotalItems;
        }
      });
      return { tree: filteredFeaturesTree, totalItems };
    },
    getDomainFromString: function (domainStr: string): DomainEnum {
      return ((Object.values(DomainEnum) as string[]).includes(domainStr) ? (domainStr as DomainEnum) : undefined);
    },
    getFeatureNodeTotalItems: function (featureNode: FeatureNode): number {
      let totalItems = 1;
      if (!!featureNode.children && !this.areFeatureChildrenIgnored(featureNode) &&
        !this.areFeatureChildrenDisplayedOnModal(featureNode)) {
        featureNode.children.forEach((childFeatureNode: FeatureNode) => {
          totalItems += this.getFeatureNodeTotalItems(childFeatureNode);
        });
      }
      return totalItems;
    },
    areFeatureChildrenIgnored: function (featureNode: FeatureNode): boolean {
      return (!!featureNode.menuConfig && !!featureNode.menuConfig.ignoreChildren);
    },
    areFeatureChildrenDisplayedOnModal: function (featureNode: FeatureNode): boolean {
      return (!!featureNode.menuConfig && !!featureNode.menuConfig.displayChildrenOnModal);
    },
    getMenuItem: function (featureNode: FeatureNode, domain: DomainEnum, section: MenuSectionEnum, index: number, totalItems: number) {
      const menuItem: any = {
        id: featureNode.id,
        label: this.$epb.messages.app.features[featureNode.id],
        icon: this.getFeatureIcon(featureNode),
        route: this.getFeatureRoute(featureNode),
        selected: this.isFeatureSelected(featureNode, domain)
      };
      if (!this.animationFinished) {
        const itemTransitionDurationInMillis = Math.min((this.animationStepDurationsInMillis[section] / totalItems), 200);
        menuItem.transition = {
          durationInMillis: itemTransitionDurationInMillis,
          delayInMillis: (itemTransitionDurationInMillis * index)
        };
      }
      index++;
      if (!!featureNode.children && !this.areFeatureChildrenIgnored(featureNode)) {
        const displayChildrenOnModal = this.areFeatureChildrenDisplayedOnModal(featureNode);
        menuItem.children = featureNode.children.map((childFeatureNode: FeatureNode) => {
          const { item: childMenuItem, index: newIndex } = this.getMenuItem(childFeatureNode, domain, section, index, totalItems);
          if (!displayChildrenOnModal) {
            index = newIndex;
          }
          return childMenuItem;
        });
        menuItem.displayChildrenOnModal = displayChildrenOnModal;
      }
      return { item: menuItem, index };
    },
    getFeatureIcon: function (featureNode: FeatureNode): string {
      return {
        [FeatureIdEnum.ANALYTICS]: 'analitics',
        [FeatureIdEnum.INSIGHTS]: 'insight',
        [FeatureIdEnum.PLAY]: 'equalize',
        [FeatureIdEnum.EXPLAIN]: 'explain',
        [FeatureIdEnum.AUDIT]: 'audit',
        [FeatureIdEnum.INDEX]: 'index',
        [FeatureIdEnum.ADMIN]: 'config',
        [FeatureIdEnum.ACCOUNT]: 'useradmin'
      }[featureNode.id];
    },
    getFeatureRoute: function (featureNode: FeatureNode) {
      if (!!featureNode.routeName) {
        return {
          ...this.$route,
          name: featureNode.routeName
        };
      }
    },
    isFeatureSelected: function (featureNode: FeatureNode, domain: DomainEnum): boolean {
      if (domain === this.context.domain) {
        if ((featureNode.id === this.$route.meta.featureId)) {
          return true;
        } else if (!!featureNode.children && this.areFeatureChildrenDisplayedOnModal(featureNode)) {
          return !!SetContextWithUrlGuard.getFeatureNode(this.$route.meta.featureId, featureNode.children);
        }
      }
    },
    onAnimationFinished: function (): void {
      this.animationFinished = true;
    }
  },
  mounted: function (): void {
    if (!this.collapsible || this.collapsed) {
      this.currentAnimationStep = MenuSectionEnum.FOOTER;
    }
  },
  watch: {
    'context.domain': {
      handler: function (domain: DomainEnum): void {
        this.selectedDomain = (domain || this.selectedDomain || this.domains[0]);
      },
      immediate: true
    }
  }
};

